import {
  BlissfulIcon,
  CrossIcon,
  Flex,
  LightningIcon,
  PainFreeIcon,
  PencilIcon,
  PizzaIcon,
  RelaxedIcon,
  SleepIcon,
  Typography,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';

import { isEffectsDisabledState } from '../../../utils/isEffectsDisabledState';
import type { WizardProps, WizardStepProps } from '../../../utils/wizard/types';
import { WizardFeeling, WizardStepKey } from '../../../utils/wizard/types';

import tileStyles from '../wizardOptionTile.module.css';
import stepStyles from '../wizardStep.module.css';
import styles from './feelingsStep.module.css';

interface FeelingsStepProps extends WizardStepProps {
  disabledFeelingTags?: string[];
}

const FEELINGS: { icon: ReactNode; label: string; value: WizardFeeling }[] = [
  {
    icon: <RelaxedIcon size="md" />,
    label: 'Relaxed',
    value: WizardFeeling.Relaxed,
  },
  {
    icon: <SleepIcon size="md" />,
    label: 'Sleepy',
    value: WizardFeeling.Sleepy,
  },
  {
    icon: <PizzaIcon size="md" />,
    label: 'Hungry',
    value: WizardFeeling.Hungry,
  },
  {
    icon: <BlissfulIcon size="md" />,
    label: 'Blissful',
    value: WizardFeeling.Blissful,
  },
  {
    icon: <CrossIcon size="md" />,
    label: 'Pain free',
    value: WizardFeeling.PainFree,
  },
  {
    icon: <LightningIcon size="md" />,
    label: 'Energetic',
    value: WizardFeeling.Energetic,
  },
  {
    icon: <PencilIcon size="md" />,
    label: 'Creative',
    value: WizardFeeling.Creative,
  },
  {
    icon: <PainFreeIcon size="md" />,
    label: 'Non-impairing',
    value: WizardFeeling.NotHigh,
  },
];

const ALL_FEELING_VALUES = FEELINGS.map((f) => f.value);
const MAX_SELECTION = 3;

export function feelingsStepEnabledForStore({
  disabledFeelingTags,
  storeState,
}: WizardProps): boolean {
  if (isEffectsDisabledState(storeState)) return false;
  return !ALL_FEELING_VALUES.every((f) => disabledFeelingTags.includes(f));
}

interface FeelingOptionProps {
  disabled: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
  selected: boolean;
}

function FeelingOption({
  disabled,
  icon,
  label,
  onClick,
  selected,
}: FeelingOptionProps) {
  const className = clsx(
    tileStyles['tile'],
    stepStyles['option'],
    styles['option'],
    selected && tileStyles['selected'],
    disabled && tileStyles['disabled']
  );

  return (
    <button
      type="button"
      className={className}
      onClick={disabled ? undefined : onClick}
      aria-pressed={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : undefined}
    >
      {icon}
      <Typography variant="body-bold">{label}</Typography>
    </button>
  );
}

export function FeelingsStep({
  disabledFeelingTags,
  form,
  updateForm,
}: FeelingsStepProps) {
  const selections = form[WizardStepKey.Feelings];
  const isMaxed = selections.length >= MAX_SELECTION;
  const visibleFeelings = FEELINGS.filter(
    ({ value }) => !disabledFeelingTags?.includes(value)
  );

  const handleToggle = (value: string) => {
    const isSelected = selections.includes(value);

    if (isSelected) {
      updateForm({
        ...form,
        [WizardStepKey.Feelings]: selections.filter((v) => v !== value),
      });
    } else if (!isMaxed) {
      updateForm({
        ...form,
        [WizardStepKey.Feelings]: [...selections, value],
      });
    }
  };

  return (
    <Flex flexDirection="column" alignItems="center" gap={32} width="100%">
      <Flex flexDirection="column" alignItems="center" gap={8}>
        <Typography variant="header-bold" textAlign="center">
          How do you want to feel?
        </Typography>
        <Typography variant="body" textAlign="center">
          Pick up to 3 effects.
        </Typography>
      </Flex>

      <div className={clsx(stepStyles['grid'], styles['grid'])}>
        {visibleFeelings.map(({ icon, label, value }) => {
          const selected = selections.includes(value);
          const disabled = !selected && isMaxed;

          return (
            <FeelingOption
              key={value}
              disabled={disabled}
              icon={icon}
              label={label}
              onClick={() => handleToggle(value)}
              selected={selected}
            />
          );
        })}
      </div>
    </Flex>
  );
}
